import { KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronLeft, Search } from 'lucide-react';
import { FOCUS_RING } from './toolbarConfig';

/**
 * Command-menu list (Linear/Notion "+ Filter"): optional search on top, a
 * listbox of 36px rows, ↑↓ / Home / End / Enter, ← or Backspace on an empty
 * query goes back, Esc closes. Used for the field list and every value picker.
 */
export interface CommandItem {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Right-aligned meta: a count or the current value. */
  meta?: ReactNode;
  /** Checkbox (multi) / check mark (single) state; undefined = navigational row. */
  selected?: boolean;
  /** Trailing chevron for rows that open a sub-picker. */
  nested?: boolean;
  /** On/off row in a non-multi list (renders a checkbox). */
  toggle?: boolean;
  onSelect: () => void;
}

interface Props {
  label: string;
  items: CommandItem[];
  /** Header with a back button (value pickers). */
  title?: string;
  onBack?: () => void;
  onClose?: () => void;
  searchable?: boolean;
  placeholder?: string;
  multi?: boolean;
  footer?: ReactNode;
}

const Box = ({ on, multi }: { on: boolean; multi?: boolean }) =>
  multi ? (
    <span
      aria-hidden
      className={`w-4 h-4 rounded-[4px] border border-solid flex items-center justify-center shrink-0 transition-colors ${
        on ? 'bg-sd-foreground border-sd-foreground text-white' : 'border-gray-300 bg-white'
      }`}
    >
      {on ? <Check className='w-3 h-3' strokeWidth={3} /> : null}
    </span>
  ) : (
    <Check
      aria-hidden
      className={`w-4 h-4 shrink-0 ${on ? 'text-sd-foreground' : 'invisible'}`}
      strokeWidth={2.5}
    />
  );

const CommandList = (props: Props) => {
  const { label, items, title, onBack, onClose, searchable, placeholder, multi, footer } = props;
  const { t } = useTranslation();
  const id = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const q = query.trim().toLowerCase();
  const shown = q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  const current = Math.min(active, Math.max(shown.length - 1, 0));

  // Next tick: the popover portal attaches after its first render.
  useEffect(() => {
    const id = setTimeout(() =>
      (inputRef.current ?? listRef.current)?.focus({ preventScroll: true }),
    );
    return () => clearTimeout(id);
  }, []);
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${current}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [current]);

  const onKeyDown = (e: KeyboardEvent) => {
    const rtl = document.documentElement.dir === 'rtl';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    const move = (n: number) => {
      e.preventDefault();
      setActive(n);
    };
    if (e.key === 'ArrowDown') move((current + 1) % Math.max(shown.length, 1));
    else if (e.key === 'ArrowUp') move((current - 1 + shown.length) % Math.max(shown.length, 1));
    else if (e.key === 'Home' && !query) move(0);
    else if (e.key === 'End' && !query) move(shown.length - 1);
    else if (e.key === 'Enter' || (e.key === ' ' && !searchable)) {
      e.preventDefault();
      shown[current]?.onSelect();
    } else if (onBack && !query && (e.key === back || e.key === 'Backspace')) {
      e.preventDefault();
      onBack();
    } else if (e.key === 'Escape' && onClose) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const activeDescendant = shown.length ? optionId(current) : undefined;

  return (
    <div className='flex flex-col min-h-0' onKeyDown={onKeyDown}>
      {title ? (
        <div className='flex items-center gap-1 h-10 px-1.5 border-0 border-b border-solid border-sd-border shrink-0'>
          {onBack ? (
            <button
              type='button'
              onClick={onBack}
              aria-label={String(t('back'))}
              className={`w-7 h-7 inline-flex items-center justify-center rounded-md bg-transparent border-0 cursor-pointer text-sd-muted-foreground hover:bg-sd-muted hover:text-sd-foreground ${FOCUS_RING}`}
            >
              <ChevronLeft aria-hidden className='w-4 h-4 rtl:rotate-180' />
            </button>
          ) : null}
          <span className='text-[12px] font-semibold text-sd-muted-foreground truncate'>
            {title}
          </span>
        </div>
      ) : null}
      {searchable ? (
        <div className='flex items-center gap-2 h-10 px-3 border-0 border-b border-solid border-sd-border shrink-0'>
          <Search aria-hidden className='w-4 h-4 text-sd-muted-foreground shrink-0' />
          <input
            ref={inputRef}
            role='combobox'
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={activeDescendant}
            aria-autocomplete='list'
            aria-label={label}
            value={query}
            placeholder={placeholder}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            className='flex-1 min-w-0 h-full border-0 bg-transparent outline-none text-[13px] text-sd-foreground placeholder:text-sd-muted-foreground'
          />
        </div>
      ) : null}
      {items.length ? (
        <ul
          ref={listRef}
          id={listId}
          role='listbox'
          aria-label={label}
          aria-multiselectable={multi || undefined}
          aria-activedescendant={searchable ? undefined : activeDescendant}
          tabIndex={searchable ? -1 : 0}
          className='m-0 p-1 list-none overflow-y-auto overscroll-contain max-h-[296px] outline-none'
        >
          {shown.map((item, i) => (
            <li
              key={item.id}
              id={optionId(i)}
              data-index={i}
              role='option'
              aria-selected={item.selected ?? false}
              onMouseMove={() => current !== i && setActive(i)}
              onClick={item.onSelect}
              className={`flex items-center gap-2.5 h-9 px-2 rounded-md cursor-pointer select-none text-[13px] text-sd-foreground ${
                i === current ? 'bg-sd-muted' : ''
              }`}
            >
              {item.selected !== undefined && (multi || item.toggle) ? (
                <Box on={item.selected} multi />
              ) : null}
              {item.icon ? (
                <span aria-hidden className='inline-flex shrink-0 text-sd-muted-foreground'>
                  {item.icon}
                </span>
              ) : null}
              <span className='flex-1 min-w-0 truncate' title={item.label}>
                {item.label}
              </span>
              {item.meta !== undefined ? (
                <span className='max-w-[60%] truncate text-[12px] text-sd-muted-foreground tabular-nums'>
                  {item.meta}
                </span>
              ) : null}
              {item.selected !== undefined && !multi && !item.toggle ? (
                <Box on={item.selected} />
              ) : null}
              {item.nested ? (
                <ChevronLeft
                  aria-hidden
                  className='w-4 h-4 shrink-0 text-sd-muted-foreground rotate-180 rtl:rotate-0'
                />
              ) : null}
            </li>
          ))}
          {shown.length === 0 && q ? (
            <li role='presentation' className='px-2 py-3 text-[13px] text-sd-muted-foreground'>
              {String(t('no_results'))}
            </li>
          ) : null}
        </ul>
      ) : null}
      {footer}
    </div>
  );
};

export default CommandList;
